import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { CapabilityDTO, CapabilityFormValues } from '../../types';
import { useAgentCapabilityAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import { UnverifiedBanner } from '../Shared/UnverifiedBanner';

const EMPTY_FORM: CapabilityFormValues = {
  CapabilityName: '',
  Description: '',
  CapabilityTypeID: 0,
  Parameters: '',
  Prerequisites: '',
  Cost: null,
  IsAdvertised: true,
};

/**
 * Restored Capabilities tab (Phase 3 of the Agent-editor sub-entity restoration — see
 * TemplatesPanel.tsx/RulesPanel.tsx/HooksPanel.tsx, this panel's direct structural template).
 * Real entity (AIAgent_Capabilities) confirmed by reading Capability.cs directly: Description
 * (not CapabilityDescription), a required CapabilityTypeID FK, Parameters/Prerequisites (JSON
 * strings), Cost, IsAdvertised — no IsEnabled/CapabilityVersion. CapabilityName (required) is a
 * real column the task briefing never mentioned — see types.ts's CapabilityDTO comment. No
 * lookup controller was found for CapabilityTypeID, so it renders as a plain required number
 * input, same treatment RulesPanel.tsx gives AgentRuleTypeID/TriggerEventID.
 */
export function CapabilitiesPanel({ agentId }: { agentId: number }) {
  const api = useAgentCapabilityAdminApiClient();
  const [capabilities, setCapabilities] = useState<CapabilityDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CapabilityDTO | 'new' | null>(null);
  const [form, setForm] = useState<CapabilityFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CapabilityDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getCapabilitiesByAgent(agentId);
    setCapabilities(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (capability: CapabilityDTO | 'new') => {
    setEditing(capability);
    setForm(
      capability === 'new'
        ? EMPTY_FORM
        : {
            CapabilityName: capability.CapabilityName,
            Description: capability.Description ?? '',
            CapabilityTypeID: capability.CapabilityTypeID,
            Parameters: capability.Parameters ?? '',
            Prerequisites: capability.Prerequisites ?? '',
            Cost: capability.Cost ?? null,
            IsAdvertised: capability.IsAdvertised,
          }
    );
  };

  const handleSave = async () => {
    if (!form.CapabilityName.trim() || !form.CapabilityTypeID) return;
    setSaving(true);
    const result = editing === 'new' ? await api.createCapability(agentId, form) : await api.updateCapability((editing as CapabilityDTO).CapabilityID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteCapability(pendingDelete.CapabilityID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setCapabilities((prev) => prev.filter((c) => c.CapabilityID !== pendingDelete.CapabilityID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <UnverifiedBanner entityName="Capabilities" />
        <div className="aaa-form-group aaa-form-group-full">
          <label>Capability Name *</label>
          <input type="text" value={form.CapabilityName} onChange={(e) => setForm((f) => ({ ...f, CapabilityName: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Description</label>
          <input type="text" value={form.Description ?? ''} onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label>Capability Type ID *</label>
          <input
            type="number"
            value={form.CapabilityTypeID || ''}
            onChange={(e) => setForm((f) => ({ ...f, CapabilityTypeID: e.target.value ? parseInt(e.target.value, 10) : 0 }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Cost</label>
          <input
            type="number"
            step={0.0001}
            value={form.Cost ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, Cost: e.target.value ? parseFloat(e.target.value) : null }))}
          />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Parameters (JSON)</label>
          <textarea rows={4} value={form.Parameters ?? ''} onChange={(e) => setForm((f) => ({ ...f, Parameters: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Prerequisites (JSON)</label>
          <textarea rows={4} value={form.Prerequisites ?? ''} onChange={(e) => setForm((f) => ({ ...f, Prerequisites: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label className="aaa-checkbox">
            <input type="checkbox" checked={form.IsAdvertised} onChange={(e) => setForm((f) => ({ ...f, IsAdvertised: e.target.checked }))} />
            Advertised
          </label>
        </div>
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Capability'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UnverifiedBanner entityName="Capabilities" />
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Capability
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && capabilities.length === 0 && <div className="aaa-entity-dropdown-empty">No capabilities yet.</div>}
      <div className="aaa-entity-grid">
        {capabilities.map((capability) => (
          <div key={capability.CapabilityID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>{capability.CapabilityName}</strong>
              <span className={`aaa-badge ${capability.IsAdvertised ? 'aaa-badge-active' : 'aaa-badge-inactive'}`}>
                {capability.IsAdvertised ? 'Advertised' : 'Not Advertised'}
              </span>
            </div>
            {capability.Description && <p>{capability.Description}</p>}
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(capability)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(capability)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Capability"
        message={`Delete capability "${pendingDelete?.CapabilityName}"?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
